import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { map, mergeMap, Observable, of, shareReplay, take } from 'rxjs';
import {
  BoardGate,
  GetAllBoardMetadataAndPersonalBoardResponse,
} from '../../../../../backend/src/core/board/board.gate';
import { CoreItemGate, GetItemListByTypeListRequest } from '../../../../../backend/src/core/core-item/core-item.gate';
import { ResourceName } from '../../../../../backend/src/core/resource-name';
import { LambdaService } from '../../shared/core/lambda.service';
import { Board } from '../board/board';
import { BoardHolderService } from '../board/board.holder.service';
import { CoreItem, CoreItemType } from '../core-item/core-item';
import { CoreItemService } from '../core-item/core-item.service';
import { SortKeyUtils } from '../core-item/sort-key';
import { RelationService } from '../relation/relation.service';
import { UserClerkService } from '../user/user-clerk.service';
import { WorkspaceHolderService } from '../workspace/workspace.holder.service';
import { WorkspaceService } from '../workspace/workspace.service';

@Injectable({ providedIn: 'root' })
export class InitAllBoardListService {
  private resourceName = ResourceName.board;

  private _cachedData?: GetAllBoardMetadataAndPersonalBoardResponse;

  isMaintainanceMode = signal<boolean>(false);
  maintainanceText = signal<string>('');

  constructor(
    private lambdaService: LambdaService,
    private router: Router,
    private relationService: RelationService,
    private coreItemService: CoreItemService,
    private boardHolderService: BoardHolderService,
    private workspaceService: WorkspaceService,
    private workspaceHolderService: WorkspaceHolderService,
    private userClerkService: UserClerkService
  ) {}

  getAllBoardMetadataAndPersonalBoardAndChangeDefaultWorkspace$(
    workspaceId?: string
  ): Observable<GetAllBoardMetadataAndPersonalBoardResponse> {
    if (this._cachedData) {
      return of(this._cachedData);
    }

    return this.lambdaService
      .run$(this.resourceName, BoardGate.actions.getAllBoardMetadataAndPersonalBoard, { workspaceId })
      .pipe(
        map((data) => {
          if (data.isMaintainanceMode) {
            this.isMaintainanceMode.set(true);
            this.maintainanceText.set(data.maintainanceText!);

            this.router.navigate(['/maintainance']);
            return;
          }

          const response = data as GetAllBoardMetadataAndPersonalBoardResponse;

          const defaultWorkspaceBackend = response.myWorkspaceList.find((c: any) => c.isDefault)!;

          const defaultWorkspace = this.workspaceService.convertFromBackendToFrontend(defaultWorkspaceBackend);
          this.workspaceHolderService.currentWorkspace.set(defaultWorkspace!);
          this.workspaceHolderService.fileList.set(response.fileList);

          const board = this.coreItemService.convertFromBackendToFrontend(response.myPersonalBoard) as Board;

          this.boardHolderService.myPersonalBoard.set(board);

          const user = this.userClerkService.getUser()!;

          if (!board.userBoardConfig || board.userBoardConfig?.preferredName === 'No Name') {
            board.userBoardConfig = {
              userId: user.id,
              email: user.primaryEmailAddress?.emailAddress,
              preferredName: user.firstName ?? 'No Name',
              lastName: user.lastName ?? '',
              portraitUrl: user.imageUrl!,
            };
            this.boardHolderService.updateMyPersonalBoard$().pipe(take(1)).subscribe();
          }

          response.itemBackendList
            .filter((c) => SortKeyUtils.parse(c.sk).coreItemType !== CoreItemType.relation)
            .map((c: any) => this.coreItemService.updateCoreItemInCache(c)) as CoreItem[];

          response.itemBackendList
            .filter((c) => SortKeyUtils.parse(c.sk).coreItemType === CoreItemType.relation)
            .map((c) => SortKeyUtils.getVertexRelation(c))
            .map((c: any) => {
              this.relationService.setRelationInCache(c);
            });

          response.loadedCoreItemTypeList?.forEach((c: CoreItemType) => {
            const request: GetItemListByTypeListRequest = {
              workspaceId: defaultWorkspace.id,
              config: {
                coreTypeList: [c],
              },
            };

            this.coreItemService.setItemOrItemListByActionInCache(CoreItemGate.actions.getItemListByTypeList, request);
          });

          this._cachedData = data;
          return data;
        }),
        shareReplay(1),
        mergeMap((data) => {
          if (!this.workspaceHolderService.currentWorkspace().timezone) {
            this.workspaceHolderService.currentWorkspace().timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            return this.workspaceService
              .updateWorkspace$(this.workspaceHolderService.currentWorkspace())
              .pipe(map(() => data));
          }
          return of(data);
        })
      );
  }
}
